import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_AGENT, GET_ALL_AGENTS } from '../graphql/agents';
import Modal from './Modal';

const buildEmpty = () => ({
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: ''
});

const QuickAddAgentModal = ({ open, onClose, onCreated }) => {
  const [state, setState] = useState(buildEmpty);
  const [errors, setErrors] = useState({});

  const [createAgent, { loading, error }] = useMutation(CREATE_AGENT, {
    refetchQueries: [{ query: GET_ALL_AGENTS }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      onCreated?.(data?.createAgent);
      setState(buildEmpty());
      setErrors({});
      onClose?.();
    },
    onError: (err) => console.error('Error creating agent:', err)
  });

  const setField = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!state.firstName.trim()) next.firstName = 'Required';
    if (!state.lastName.trim()) next.lastName = 'Required';
    if (!state.email.trim()) next.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) next.email = 'Invalid email';
    if (!state.primaryPhone.trim()) next.primaryPhone = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    createAgent({
      variables: {
        input: {
          firstName: state.firstName.trim(),
          lastName: state.lastName.trim(),
          email: state.email.trim(),
          primaryPhone: state.primaryPhone.trim()
        }
      }
    });
  };

  const inputClass = (k) =>
    `block w-full px-3 py-2 border ${errors[k] ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`;

  return (
    <Modal open={open} onClose={onClose} title="Add Agent" size="md">
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
          {error.message}
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name*</label>
            <input
              type="text"
              value={state.firstName}
              onChange={(e) => setField('firstName', e.target.value)}
              className={inputClass('firstName')}
              autoFocus
            />
            {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name*</label>
            <input
              type="text"
              value={state.lastName}
              onChange={(e) => setField('lastName', e.target.value)}
              className={inputClass('lastName')}
            />
            {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
            <input
              type="email"
              value={state.email}
              onChange={(e) => setField('email', e.target.value)}
              className={inputClass('email')}
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Primary Phone*</label>
            <input
              type="tel"
              value={state.primaryPhone}
              onChange={(e) => setField('primaryPhone', e.target.value)}
              className={inputClass('primaryPhone')}
            />
            {errors.primaryPhone && <p className="mt-1 text-xs text-red-600">{errors.primaryPhone}</p>}
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Only the essentials. Add FICA, address and other details later from the Agents page.
        </p>

        <div className="flex justify-end space-x-2 pt-2 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Adding…' : 'Add Agent'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default QuickAddAgentModal;
